import React, { useCallback, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';

import { closeMonthDrawerSections } from '@common/constants';
import { useCloseMonth } from '@common/slices';
import { CloseMonthForm } from '@common/types';
import DataService from '@core/services/DataService';
import { RootState } from '@core/redux/store';


import { useFormik } from 'formik';
import * as Yup from 'yup';
import DynamicFormWidget from '@components/dynamic-form/DynamicFormWidget';

interface Props {
  onSuccess?: () => void;
  monthClosingTypeLovs: Record<string, string>;
}

const CloseMonthDrawer: React.FC<Props> = ({
  onSuccess,
  monthClosingTypeLovs
}) => {
  const buttonState = useSelector(
    (state: RootState) => state.apiState.buttonState
  );
  const userInfo = useSelector((state: RootState) => state.user.info);

  const [closingTypeID, setClosingTypeID] = useState('');
  const closingTypeTitle = monthClosingTypeLovs[closingTypeID] ?? '';
  const [closeMonth] = useCloseMonth(closingTypeTitle);

  const formik = useFormik<CloseMonthForm>({
    initialValues: {
      closingTypeID: '',
      closingPeriodFrom: '',
      closingPeriodTo: ''
    },
    validationSchema: Yup.object({
      closingTypeID: Yup.string().required('Required'),
      closingPeriodFrom: Yup.date().required('Required'),
      closingPeriodTo: Yup.date()
        .required('Required')
        .min(
          Yup.ref('closingPeriodFrom'),
          'Must be on or after Closing Period From'
        )
    }),
    onSubmit: async (values) => {
      const toUTCMidnight = (dateStr: string) => {
        const d = new Date(dateStr);
        const y = d.getFullYear();
        const m = String(d.getMonth() + 1).padStart(2, '0');
        const day = String(d.getDate()).padStart(2, '0');
        return `${y}-${m}-${day}T00:00:00.000Z`;
      };
      const result = await closeMonth({
        closeMonthInput: {
          closingTypeID: values.closingTypeID,
          closingPeriodFrom: toUTCMidnight(values.closingPeriodFrom),
          closingPeriodTo: toUTCMidnight(values.closingPeriodTo)
        }
      });
      const file = result?.data?.reinsurance?.closeMonth;
      if (file) {
        DataService.downloadBase64File(
          file.content,
          file.contentType,
          file.fileName
        );
        onSuccess?.();
        formik.resetForm();
      }
    }
  });

  const handleChange = useCallback(
    (fieldName: string, value: string | number | boolean) => {
      if (fieldName === 'closingTypeID') {
        setClosingTypeID(value as string);
      }
      formik.setFieldValue(fieldName, value);
      formik.setFieldTouched(fieldName, true);
    },
    [formik]
  );

  const sections = useMemo(
    () =>
      closeMonthDrawerSections(
        formik,
        { monthClosingTypes: monthClosingTypeLovs },
        {
          reinsuranceClosingRights:
            userInfo?.businessUser_ReinsuranceClosingRights ?? false,
          productionClosingRights:
            userInfo?.businessUser_ProductionClosingRights ?? false
        }
      ),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [formik.values, monthClosingTypeLovs, userInfo]
  );

  return (
    <DynamicFormWidget
      sections={sections}
      onChange={handleChange}
      onSubmit={() => formik.handleSubmit()}
      submitButtonState={buttonState}
    />
  );
};

export { CloseMonthDrawer };
